import * as React from "react";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";


type BYOKStatus = {
  configured: boolean;
  endpointOverride: string | null;
  provider: string | null;
};

export default function AIProviderSettings() {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const { displayToast } = useToastAlert();

  // Status from the BYOK endpoint (always fresh on mount)
  const [status, setStatus] = React.useState<BYOKStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = React.useState(true);

  // Configure form state
  const [configExpanded, setConfigExpanded] = React.useState(false);
  const [provider, setProvider] = React.useState<"azure" | "openai">("openai");
  const [apiKey, setApiKey] = React.useState("");
  const [endpointOverride, setEndpointOverride] = React.useState("");
  const [pricingAcknowledged, setPricingAcknowledged] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  // Removal state
  const [removalExpanded, setRemovalExpanded] = React.useState(false);
  const [requestingRemoval, setRequestingRemoval] = React.useState(false);
  const [removalSubmitted, setRemovalSubmitted] = React.useState(false);

  const isConfigured = status?.configured ?? subscription?.aiApiKeyConfigured ?? false;

  React.useEffect(() => {
    async function loadStatus() {
      try {
        const res = await api.messaging.get("BYOK/status");
        if (res.ok) {
          const data: BYOKStatus = await res.json();
          setStatus(data);
          if (data.configured) {
            setConfigExpanded(false);
            if (data.provider === "Azure OpenAI") setProvider("azure");
          }
        }
      } catch {
        // fall back to subscription data
      } finally {
        setLoadingStatus(false);
      }
    }
    loadStatus();
  }, []);

  const canSave = pricingAcknowledged && apiKey.trim().length > 0;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    try {
      const body = {
        apiKey: apiKey.trim(),
        endpointOverride: provider === "azure" ? endpointOverride.trim() || null : null
      };
      const res = await api.messaging.post("BYOK/configure", body);
      if (res.ok) {
        const data = await res.json();
        setStatus({
          configured: true,
          endpointOverride: data.endpointOverride ?? null,
          provider: data.endpointOverride ? "Azure OpenAI" : "OpenAI Direct"
        });
        setApiKey("");
        setPricingAcknowledged(false);
        setConfigExpanded(false);
        displayToast(Severity.Success, "Custom AI key saved successfully.");
      } else {
        const err = await res.json().catch(() => ({}));
        displayToast(Severity.Error, (err as any)?.error ?? "Failed to save API key.");
      }
    } catch {
      displayToast(Severity.Error, "Failed to save API key. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleRequestRemoval() {
    setRequestingRemoval(true);
    try {
      const res = await api.messaging.post("BYOK/request-removal", {});
      if (res.ok) {
        setRemovalSubmitted(true);
        displayToast(Severity.Success, "Removal request submitted.");
      } else {
        const err = await res.json().catch(() => ({}));
        displayToast(Severity.Error, (err as any)?.error ?? "Failed to submit removal request.");
      }
    } catch {
      displayToast(Severity.Error, "Failed to submit removal request. Please try again.");
    } finally {
      setRequestingRemoval(false);
    }
  }

  if (loadingStatus) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400 text-sm">
        Loading AI provider settings…
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-base font-semibold text-slate-800">AI Provider</h2>
        <p className="mt-1 text-sm text-slate-500">
          By default, all AI features use Velaro&apos;s shared infrastructure. You can optionally
          supply your own OpenAI or Azure OpenAI key — your key is used for all bot and workflow AI
          calls on your account.
        </p>
      </div>

      {/* ── A) STATUS BANNER ─────────────────────────────────────────── */}
      {isConfigured ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm">
          <p className="font-medium text-amber-800">
            Your account uses a custom AI key.
          </p>
          <p className="mt-1 text-amber-700">
            All bot and workflow AI calls use your key.
          </p>
          {(status?.endpointOverride ?? subscription?.aiEndpointOverride) && (
            <p className="mt-2 text-amber-700 font-mono text-xs break-all">
              Azure endpoint: {status?.endpointOverride ?? subscription?.aiEndpointOverride}
            </p>
          )}
          <p className="mt-2 text-amber-600 text-xs">
            Provider: {status?.provider ?? (subscription?.aiEndpointOverride ? "Azure OpenAI" : "OpenAI Direct")}
          </p>
        </div>
      ) : (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          Using Velaro&apos;s shared AI infrastructure (included in your plan).
        </div>
      )}

      {/* ── B) CONFIGURE SECTION ─────────────────────────────────────── */}
      <div className="rounded-md border border-slate-200 bg-white">
        <button
          type="button"
          onClick={() => setConfigExpanded((v) => !v)}
          className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <span>{isConfigured ? "Update custom AI key" : "Configure custom AI key"}</span>
          <svg
            className={`h-4 w-4 text-slate-400 transition-transform ${configExpanded ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {configExpanded && (
          <div className="border-t border-slate-200 px-4 py-4 space-y-4">
            {/* Provider select */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                AI Provider
              </label>
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value as "azure" | "openai")}
                className="block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="openai">OpenAI Direct</option>
                <option value="azure">Azure OpenAI</option>
              </select>
            </div>

            {/* Azure endpoint (conditional) */}
            {provider === "azure" && (
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Azure OpenAI Endpoint
                </label>
                <input
                  type="url"
                  value={endpointOverride}
                  onChange={(e) => setEndpointOverride(e.target.value)}
                  placeholder="https://your-resource.openai.azure.com/"
                  className="block w-full rounded border border-slate-300 px-3 py-2 text-sm placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            )}

            {/* API key input */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                API Key
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Paste API key — stored encrypted, never shown again"
                autoComplete="new-password"
                className="block w-full rounded border border-slate-300 px-3 py-2 text-sm placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Pricing disclaimer */}
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
              <p className="font-semibold mb-1">Pricing adjustment notice</p>
              <p>
                By providing your own AI key, your Velaro pricing will be adjusted to exclude AI
                compute costs — you pay your provider directly. If you later remove this key,
                standard pricing is restored. Pricing changes require contacting your Velaro account rep.
              </p>
            </div>

            {/* Acknowledgment checkbox */}
            <label className="flex items-start gap-2 cursor-pointer text-sm text-slate-700">
              <input
                type="checkbox"
                checked={pricingAcknowledged}
                onChange={(e) => setPricingAcknowledged(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              I understand that pricing will be adjusted
            </label>

            {/* Save button */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSave || saving}
                className="inline-flex items-center rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? "Saving…" : "Save Key"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── C) REMOVAL SECTION (only when configured) ────────────────── */}
      {isConfigured && (
        <div className="rounded-md border border-slate-200 bg-white">
          <button
            type="button"
            onClick={() => setRemovalExpanded((v) => !v)}
            className="flex w-full items-center justify-between px-4 py-3 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <span>Want to remove your custom key?</span>
            <svg
              className={`h-4 w-4 text-slate-400 transition-transform ${removalExpanded ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {removalExpanded && (
            <div className="border-t border-slate-200 px-4 py-4 space-y-3">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                <p className="font-medium text-slate-700 mb-1">Before you request removal:</p>
                <p>
                  This reverts all AI calls to Velaro&apos;s infrastructure and restores standard
                  pricing. Requires coordination with your account rep — Velaro removes the key
                  after adjusting your subscription.
                </p>
              </div>

              {removalSubmitted ? (
                <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700 font-medium">
                  Request submitted. A Velaro rep will contact you within 1 business day.
                </div>
              ) : (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleRequestRemoval}
                    disabled={requestingRemoval}
                    className="inline-flex items-center rounded border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-600 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {requestingRemoval ? "Submitting…" : "Request Key Removal"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
